import type { DocumentShareCredentials } from '@/entities/clinicalDocument/documentShareCredentials'
import { buildPublicDocumentShareUrl } from '@/shared/lib/publicDocumentShareUrl'

/**
 * Monta o texto de compartilhamento pronto para WhatsApp: rótulo, link em linha
 * própria (vira preview clicável) e o código de acesso — sempre juntos no mesmo
 * texto, nunca em campos separados (ver `shareClinicalDocumentLink` abaixo).
 */
export const formatClinicalDocumentShareText = (
  label: string,
  creds: DocumentShareCredentials,
): string => {
  const url = buildPublicDocumentShareUrl(creds.token)
  const safeLabel = label.trim() || 'Documento clínico'
  return [
    safeLabel,
    '',
    'Abra o documento digital:',
    url,
    '',
    `Código de acesso: ${creds.unlockCode}`,
  ].join('\n')
}

/**
 * Copia o texto formatado e abre o seletor nativo (WhatsApp, e-mail, etc.).
 * A cópia vem primeiro para o app destino ter o payload mesmo se ignorar `text`.
 */
export const shareClinicalDocumentLink = async (
  label: string,
  creds: DocumentShareCredentials,
): Promise<'shared' | 'copied'> => {
  const text = formatClinicalDocumentShareText(label, creds)
  const title = label.trim() || 'Documento clínico'
  let copied = false
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text)
      copied = true
    } catch {
      /* clipboard pode falhar em HTTP ou sem permissão — segue para o seletor */
    }
  }
  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      // Apenas title + text: passar `url` junto é problemático em vários alvos
      // Android (inclusive WhatsApp), que tratam text/url como campos concorrentes
      // e podem descartar um dos dois — arriscando enviar o link sem o código.
      await navigator.share({ title, text })
      return 'shared'
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return 'shared'
      }
    }
  }
  if (copied) return 'copied'
  throw new Error('Compartilhamento não disponível neste dispositivo.')
}

export const ACTIVE_PUBLIC_LINK_WITHOUT_CREDS_MESSAGE =
  'Não foi possível copiar o link deste documento neste aparelho.'

/**
 * Compartilha o link público (sessão → existente → mint). Não revoga.
 * Lança AppError 409 quando há link ativo sem credenciais na sessão.
 */
/** Texto pronto (link + código) sem abrir o seletor. Usado junto com o PDF. */
export const loadClinicalDocumentShareText = async (
  documentId: string,
  label: string,
): Promise<string> => {
  const { clinicalDocumentShareTokensRepository } = await import(
    '@/shared/repositories/clinicalDocumentShareTokens/clinicalDocumentShareTokensRepository'
  )
  const { AppError } = await import('@/shared/lib/appError')

  const existing = await clinicalDocumentShareTokensRepository.getExisting(documentId)
  if (existing.credentials) return formatClinicalDocumentShareText(label, existing.credentials)
  if (existing.canEnableLink) {
    const creds = await clinicalDocumentShareTokensRepository.enable(documentId)
    return formatClinicalDocumentShareText(label, creds)
  }
  if (existing.hasActiveLink) {
    throw new AppError(ACTIVE_PUBLIC_LINK_WITHOUT_CREDS_MESSAGE, { status: 409, code: 'CONFLICT' })
  }

  const sessionCreds = clinicalDocumentShareTokensRepository.getMintedThisSession(documentId)
  if (sessionCreds) return formatClinicalDocumentShareText(label, sessionCreds)

  const creds = await clinicalDocumentShareTokensRepository.mint(documentId)
  return formatClinicalDocumentShareText(label, creds)
}

export const shareOrMintPublicDocumentLink = async (
  documentId: string,
  label: string,
): Promise<'shared' | 'copied'> => {
  const { clinicalDocumentShareTokensRepository } = await import(
    '@/shared/repositories/clinicalDocumentShareTokens/clinicalDocumentShareTokensRepository'
  )
  const { AppError } = await import('@/shared/lib/appError')

  const existing = await clinicalDocumentShareTokensRepository.getExisting(documentId)
  if (existing.credentials) return shareClinicalDocumentLink(label, existing.credentials)
  if (existing.canEnableLink) {
    const creds = await clinicalDocumentShareTokensRepository.enable(documentId)
    return shareClinicalDocumentLink(label, creds)
  }
  if (existing.hasActiveLink) {
    throw new AppError(ACTIVE_PUBLIC_LINK_WITHOUT_CREDS_MESSAGE, { status: 409, code: 'CONFLICT' })
  }

  const sessionCreds = clinicalDocumentShareTokensRepository.getMintedThisSession(documentId)
  if (sessionCreds) return shareClinicalDocumentLink(label, sessionCreds)

  const creds = await clinicalDocumentShareTokensRepository.mint(documentId)
  return shareClinicalDocumentLink(label, creds)
}
